import { Injectable } from '@angular/core';
import { Urls } from '@core/config/urls';
import { KeyValue } from '@smartera/core';
import { LabelValue } from '@smartera/core';
import { User } from '@api/models/user/user';
import { RestClientService } from '@smartera/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
	providedIn: 'root',
})
export class CentralSkuComboService {
	public apiUrl: string = Urls.BASE_URL.toString() + '/sku/combo';
	constructor(private _http: RestClientService) {}

	public getBrandsList(): Observable<KeyValue<string>[]> {
		const body = {
			valid: true,
		};
		return this._http.post(`${this.apiUrl}/brand`, JSON.stringify(body)).pipe(
			map((arr) =>
				arr.map((item) => {
					return { label: item.name, value: item.code };
				})
			)
		);
	}

	public patternMapper(
		res: { code: string; name: string; additionalFiles?: any }[],
		pattern: string
	): LabelValue<string, string>[] {
		const codeNameMapper = (i) => {
			return { label: i.name, value: i.code };
		};
		// check if pattern has pattern keys
		if (pattern) {
			// get pattern keys {osmething} , {lastName} , {name}
			const keys = pattern.match(/\{\w*\}/g);
			if (keys.length) {
				const result: any[] = [];
				// remove pattern syntax
				const keysValues = keys.map((k) => k.replace(/[\{\}]/g, ''));

				res.forEach((item) => {
					const value = item.code;
					let label = pattern;

					const target = item.additionalFiles;

					// set patterns keys value from  additionalFiles
					for (let i = 0; i < keys.length; i++) {
						const key = keys[i];
						const keyValue = keysValues[i];

						label = label.replace(key, target[keyValue]);
					}

					result.push({
						label,
						value,
					});
				});

				return result;
			}
		}
		// def flow
		return res.map(codeNameMapper);
	}

	public getModelsList(brandId): Observable<KeyValue<string>[]> {
		const body = {
			brandId,
			valid: true,
		};
		return this._http.post(`${this.apiUrl}/brand/${brandId}/model`, JSON.stringify(body)).pipe(
			map((arr) =>
				arr.map((item) => {
					return { label: item.name, value: item.code };
				})
			)
		);
	}

	public getVersionsList(brandId, modelId): Observable<KeyValue<string>[]> {
		const body = {
			brandId,
			modelId,
			valid: true,
		};
		return this._http.post(`${this.apiUrl}/brand/${brandId}/model/${modelId}/version`, JSON.stringify(body)).pipe(
			map((arr) =>
				arr.map((item) => {
					return { label: item.name, value: item.code };
				})
			)
		);
	}

	public getSerialsList(brandId, modelId, versionId): Observable<KeyValue<string>[]> {
		const body = {
			brandId,
			modelId,
			versionId,
			valid: true,
		};
		return this._http
			.post(`${this.apiUrl}/brand/${brandId}/model/${modelId}/version/${versionId}/serial`, JSON.stringify(body))
			.pipe(
				map((arr) =>
					arr.map((item) => {
						return { label: item.name, value: item.code };
					})
				)
			);
	}

	public getGroupHierarchysList(args = {}): Observable<KeyValue<string>[]> {
		const user: User = JSON.parse(localStorage.getItem('registered_user') || '{}');
		const body = {
			valid: true,
			hasSubLevel: false,
			dealerId: user.dealerId,
			...args,
		};
		return this._http
			.post(`${this.apiUrl}/group-hierarchy?page=0&size=1000&sort=creDate,DESC`, JSON.stringify(body))
			.pipe(
				map((arr) =>
					arr.map((item) => {
						return { label: item.name, value: item.code };
					})
				)
			);
	}

	public getGroupHierarchy(scope) {
		const body = {
			// scope,
			valid: true,
		};
		return this._http
			.post(`${this.apiUrl}/group-hierarchy?page=0&size=1000&sort=creDate,DESC`, JSON.stringify(body))
			.pipe(
				map((arr) =>
					arr.map((item) => {
						return { label: item.name, value: item.code };
					})
				)
			);
	}

	public getUnitOfMeasurement(type?): Observable<KeyValue<string>[]> {
		const body = {
			// valid: true
			type,
		};
		return this._http.post(`${this.apiUrl}/unit-of-measurement`, JSON.stringify(body)).pipe(
			map((arr) =>
				arr.map((item) => {
					return { label: item.value, value: item.key };
				})
			)
		);
	}

	public getSkuUmList(skuId, raw?, availableToSale?): Observable<KeyValue<string>[]> {
		const body = {
			skuId,
			valid: true,
			availableToSale,
		};
		return this._http.post(`${this.apiUrl}/sku/${skuId}/um`, JSON.stringify(body)).pipe(
			map((arr) =>
				raw
					? arr
					: arr.map((item) => {
							return { label: item.additionalData.umName, value: item.name };
						})
			)
		);
	}

	public getSkuDef(scope) {
		const body = {
			// scope,
			valid: true,
		};
		return this._http.post(`${this.apiUrl}/sku`, JSON.stringify(body)).pipe(
			map((arr) =>
				arr.map((item) => {
					return { code: item.name, value: item.code };
				})
			)
		);
	}

	public getVariantList() {
		const body = {
			valid: true,
		};
		return this._http.post(`${this.apiUrl}/variant`, JSON.stringify(body)).pipe(
			map((arr) =>
				arr.map((item) => {
					return { label: item.name, value: item.code };
				})
			)
		);
	}

	public getVariantDetailList(variantId: string) {
		const body = {
			valid: true,
		};
		return this._http.post(`${this.apiUrl}/variant/` + variantId + '/detail', JSON.stringify(body)).pipe(
			map((arr) =>
				arr.map((item) => {
					return { label: item.name, value: item.code };
				})
			)
		);
	}

	public getSkuList(name?: string, searchObject = null) {
		const body = {
			valid: true,
			name,
			...searchObject,
		};
		return this._http.post(`${this.apiUrl}/sku?size=300`, JSON.stringify(body)).pipe(
			map((arr) =>
				arr.map((item) => {
					return { label: item.name, value: item.code };
				})
			)
		);
	}
	public getSkuListByFullCriterion(fullSearchCriterion?: string, searchObject = null, config = { map: true }) {
		const body = {
			valid: true,
			fullSearchCriterion,
			...searchObject,
		};
		return this._http.post(`${this.apiUrl}/sku`, JSON.stringify(body)).pipe(
			map((arr) => {
				if (config.map) {
					return arr.map((item) => {
						return {
							label: item.additionalData.code + item.name,
							value: item.code,
						};
					});
				} else {
					return arr;
				}
			})
		);
	}

	public getSkuListByFullCriterionByCodeFormat(fullSearchCriterion?: string, searchObject = null) {
		const body = {
			valid: true,
			fullSearchCriterion,
			...searchObject,
		};
		return this._http.post(`${this.apiUrl}/sku`, JSON.stringify(body)).pipe(
			map((arr) => {
				return arr.map((item) => {
					return {
						label: `${item.additionalData.code} > ${item.name}`,
						value: item.code,
					};
				});
			})
		);
	}

	public getSetListByFullCriterion(fullSearchCriterion?: string, searchObject = null, config = { map: true }) {
		const body = {
			valid: true,
			fullSearchCriterion,
			...searchObject,
		};
		return this._http.post(`${Urls.BASE_URL}/main/combo/set`, JSON.stringify(body)).pipe(
			map((res) =>
				res.map((item) => {
					return {
						...item,
						name: `${item.additionalData.setCode} > ${item.name}`,
					};
				})
			),
			map((content) => {
				if (config.map) {
					return content.map((item) => {
						return { label: item.name, value: item.code };
					});
				} else {
					return content;
				}
			})
		);
	}

	public getKdv(): Observable<KeyValue<string>[]> {
		const body = {
			taxType: 'KDV',
			valid: true,
		};
		return this._http.post(`${this.apiUrl}/tax`, JSON.stringify(body)).pipe(
			map((arr) =>
				arr.map((item) => {
					return { label: item.name, value: item.code };
				})
			)
		);
	}

	public getOtv(): Observable<KeyValue<string>[]> {
		const body = {
			taxType: 'OTV',
			valid: true,
		};
		return this._http.post(`${this.apiUrl}/tax`, JSON.stringify(body)).pipe(
			map((arr) =>
				arr.map((item) => {
					return { label: item.name, value: item.code };
				})
			)
		);
	}

	public getMtv(): Observable<KeyValue<string>[]> {
		const body = {
			taxType: 'MTV',
			valid: true,
		};
		return this._http.post(`${this.apiUrl}/tax`, JSON.stringify(body)).pipe(
			map((arr) =>
				arr.map((item) => {
					return { label: item.name, value: item.code };
				})
			)
		);
	}

	public getGroupHierarchyRaw(scope: string): Observable<IGroupHierarchyRaw[]> {
		const body = {
			scope,
			valid: true,
		};
		return this._http.post(`${this.apiUrl}/group-hierarchy`, JSON.stringify(body));
	}
}

export interface IGroupHierarchyRaw {
	code: string;
	name: string;
	additionalData: { regularTaxRequired: boolean; specialTaxRequired: boolean };
}
